import { FC } from 'react'
import { Bookmark } from 'lucide-react'
import { CardAction } from 'components/feed/Feed/Footer/CardAction'
import useActionGuard from 'hooks/useActionGuard'
import { TooltipWrapper } from '@wellesley/ui'
import {
  useDeleteApiV1PostsByPostIdBookmarkMutation,
  usePostApiV1PostsByPostIdBookmarkMutation,
} from '../api'
import { useSelector } from 'react-redux'
import { bookmarksSlice, BookmarksState } from '../slices/bookmarksSlice'

type BookmarkButtonProps = {
  postId: string
  isBookmarked?: boolean
  onHighlight?: (postId: string | null) => void
}

const BookmarkButton: FC<BookmarkButtonProps> = ({
  postId,
  isBookmarked,
  onHighlight,
}) => {
  const isSavedPost = useSelector((state: { bookmarks: BookmarksState }) =>
    bookmarksSlice.selectors.selectIsSavedPost(state, postId),
  )

  const isRemovedPost = useSelector((state: { bookmarks: BookmarksState }) =>
    bookmarksSlice.selectors.selectIsRemovedPost(state, postId),
  )

  const isActive = (isBookmarked || isSavedPost) && !isRemovedPost

  const { isDisabled, guardedAction, ActionTooltipWrapper } = useActionGuard()

  const [addToBookmarks] = usePostApiV1PostsByPostIdBookmarkMutation()
  const [removeFromBookmarks] = useDeleteApiV1PostsByPostIdBookmarkMutation()

  const toggle = guardedAction(() => {
    if (isActive) {
      void removeFromBookmarks({ postId })
    } else {
      void addToBookmarks({ postId })
      onHighlight?.(postId)
    }
  })

  return (
    <ActionTooltipWrapper>
      <CardAction name="bookmark" handler={toggle} disabled={isDisabled}>
        <TooltipWrapper
          theme="dark"
          size="small"
          tooltipText={
            isDisabled
              ? ''
              : `${isActive ? 'Remove from bookmarks' : 'Add to bookmarks'}`
          }
        >
          <Bookmark
            size={20}
            className="cursor-pointer"
            color={isActive ? 'var(--blue-500)' : 'var(--gray-600)'}
            {...(isActive && {
              fill: 'var(--blue-500)',
              stroke: 'var(--blue-500)',
            })}
          />
        </TooltipWrapper>
      </CardAction>
    </ActionTooltipWrapper>
  )
}

export default BookmarkButton
